// ProductDetails.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./ProductDetails.css";
import { emitAddedToCart } from "../../components/AddedToCart/AddedToCart";
import { SkeletonProduct } from "../../components/Skeleton/Skeleton";
import RelatedProduct from "../RelatedProduct/RelatedProduct";
import Reviews from "../Reviews/Reviews";
import NavBar from "../../components/NavBar/NavBar";
import Swal from "sweetalert2";
import Loaderdots from "../../components/LoaderDots/Loaderdots";
import NoData from "../../components/NoData/NoData";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FaFacebookF, FaTwitter, FaWhatsapp, FaPinterestP, FaLink } from 'react-icons/fa';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import SizeGuid from "../SideGuide/SizeGuid";
import { responsiveProps, buildSrcSet } from "../../utils/imageSrc";

/* ── How wide the two galleries ACTUALLY render ──────────────────────────
   Only ONE of them is mounted at a time: `isWide` is a matchMedia on 900px,
   so the stacked gallery exists only at >=900px and the carousel only below.

   DESKTOP STACK (.pd-gallery-img)
     .pd-layout is `minmax(0, 1fr) minmax(380px, 480px)`. The non-flexible
     track maximises to its 480px growth limit before the fr track takes the
     remainder, so the right panel is 480px and the left column is the rest.
     .pd-gallery adds 6px of padding on each side and .pd-gallery-img is
     width: 100%:
         >1024px   100vw - 480 - 12  = calc(100vw - 492px)
     The tablet rule (769-1024px) narrows the panel to minmax(320px, 400px):
         <=1024px  100vw - 400 - 12  = calc(100vw - 412px)
     (Below 900px this branch is not rendered at all, so nothing narrower
     needs describing.)

   MOBILE CAROUSEL (.pd-carousel-img)
     .pd-carousel is width: 100% with max-width: 460px inside .pd-media,
     which pads 24px per side above 768px and 16px per side below, where the
     max-width also lifts to 100%:
         <=768px   calc(100vw - 32px)
         769-899   min(100vw - 48px, 460px), which at 769px is already the
                   460px cap, so: 460px
     `object-fit: contain` letterboxes the 4:5 photo inside the 1:1 frame, so
     the painted pixels are ~0.8x this — but `sizes` describes the element
     box, which is what the browser matches against, and erring wide is the
     safe direction.

   ADDED-TO-CART PANEL thumbnail is a flat 68px (AddedToCart.css). */
const PD_GALLERY_SIZES =
  "(max-width: 1024px) calc(100vw - 412px), calc(100vw - 492px)";
const PD_CAROUSEL_SIZES =
  "(max-width: 768px) calc(100vw - 32px), 460px";

function ProductDetails() {
  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProduct] = useState([]);
  const [error, setError] = useState(null);
  const [selectedSize, setSelectedSize] = useState("");
  const [isDisable, setDisable] = useState(false);
  const [selectedColor, setColor] = useState("");
  const [stockStatusMessage, setStockStatusMessage] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  // A logged-out Add-to-Cart stashes its selection and routes the shopper to
  // login; on return we finish the add automatically instead of dropping them
  // on the homepage. See handleAddToCart and the two effects after it.
  const [pendingReplay, setPendingReplay] = useState(false);
  const replayedRef = useRef(false);

  // Desktop gets every photo stacked in a scrolling column beside a panel that
  // stays put; a phone keeps the swipe carousel. Two genuinely different
  // layouts, chosen here rather than rendered together and hidden with CSS --
  // a browser downloads images inside a display:none container just the same,
  // so doing it that way would double the image traffic on every product page.
  //
  // Read synchronously on first render, so the correct layout is the first
  // thing painted and there is no flash of the wrong one.
  const [isWide, setIsWide] = useState(
    () => typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("(min-width: 900px)").matches
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(min-width: 900px)");
    const onChange = (e) => setIsWide(e.matches);
    // addListener is the deprecated form, still needed for older Safari.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  const touchStartX = useRef(null);
  // The carousel, so the add handler can measure the photo the visitor is
  // actually looking at and throw a clone of it into the cart.
  const carouselRef = useRef(null);

  const token = localStorage.getItem("userToken");
  const navigate = useNavigate();

  const shareUrl = window.location.href;
  const shareTitle = product?.name || "Check out this product!";
  const shareImage = product?.images[0]?.url
    ? `https://riymay.com/assets/${product.images[0].url}`
    : '';

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "facebook-share-dialog", "width=626,height=436");
    setIsShareOpen(false);
  };
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`, "twitter-share-dialog", "width=626,height=436");
    setIsShareOpen(false);
  };
  const shareToWhatsApp = () => {
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`, "whatsapp-share-dialog", "width=626,height=436");
    setIsShareOpen(false);
  };
  const shareToPinterest = () => {
    window.open(`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}&media=${encodeURIComponent(shareImage)}&description=${encodeURIComponent(shareTitle)}`, "pinterest-share-dialog", "width=626,height=436");
    setIsShareOpen(false);
  };

  useEffect(() => {
    fetch(`https://riymay.com/api/v1/user/products/${id}/reviews`)
      .then(r => r.json())
      .then(data => { setReviews(data.productReviewRating || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const checkStockStatus = (size, color) => {
    if (!size || !color) { setStockStatusMessage(""); return; }
    const match = product.stock.find(s => s.size === size && s.color === color);
    if (!match || match.stockStatus !== "inStock") setStockStatusMessage("Out of Stock");
    else if (parseInt(match.quantity, 10) === 0) setStockStatusMessage("Out of Stock");
    else setStockStatusMessage("In Stock");
  };

  useEffect(() => { checkStockStatus(selectedSize, selectedColor); }, [selectedSize, selectedColor]);

  const handleSizeChart = () => navigate("/size-guide", { state: { id } });

  useEffect(() => {
    fetch(`https://riymay.com/api/v1/user/products/product?id=${id}`)
      .then(res => { if (!res.ok) throw new Error("Failed to fetch"); return res.json(); })
      .then(data => { setProduct(data.product); setRelatedProduct(data.relatedProducts); })
      .catch(err => setError(err.message));
  }, [id]);

  const handleAddToCart = async (id) => {
    if (loading || isDisable) return;
    // Size and colour are checked BEFORE login, so a logged-out shopper makes a
    // complete choice first -- and that exact choice is what we carry through
    // the login and finish for them on return.
    if (!selectedSize || !selectedColor) {
      toast.warning("Please Select a Size & Color Before Adding to Cart.", { toastId: "CArtSize", position: "top-right", autoClose: 3000, theme: "colored" });
      return;
    }
    const matchingStock = product.stock.find(s => s.size === selectedSize && s.color === selectedColor && s.stockStatus === "inStock");
    if (!matchingStock || parseInt(matchingStock.quantity, 10) === 0) {
      toast.error(`The selected size (${selectedSize}) and color (${selectedColor}) is out of stock.`, { toastId: "utOfStack", autoClose: 3000, theme: "colored" });
      return;
    }
    if (!token) {
      // The sale-losing moment: a logged-out shopper who picked a size and
      // tapped Add used to be sent to login and then dumped on the homepage,
      // their choice gone. Remember exactly what they were adding, send them
      // back to THIS product after login, and the effect below finishes the
      // add for them -- the item lands in the bag on its own.
      try {
        localStorage.setItem(
          "riymay_pending_add",
          JSON.stringify({ productId: id, size: selectedSize, color: selectedColor })
        );
      } catch (e) { /* storage blocked -- they just re-tap Add after login */ }
      toast.error("Please log in to add this to your bag.", { position: "top-right", autoClose: 3000, className: "custom-toast" });
      navigate("/login", { state: { from: { pathname: `/product/${id}` } } });
      return;
    }
    // In-flight guard. This used to be set to true only AFTER a successful add
    // and was never cleared, so the button became a permanent spinner: a
    // customer could add one item and then could not add a second size or
    // colour of the same product without reloading the page. Set it before the
    // request and clear it in `finally`, which is what a loading flag is for.
    setDisable(true);
    try {
      const res = await fetch("https://riymay.com/api/v1/user/cart", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ size: selectedSize, color: selectedColor, productId: id }),
      });
      const data = await res.json();
      if (data.message === "email is not verfied") {
        try {
          const r = await fetch("https://riymay.com/api/v1/user/resend/verification/email", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ token }),
          });
          const d = await r.json();
          if (d.email) navigate("/otp-verification", { state: { email: d.email } });
          else toast.error(d.message || "Failed to resend OTP");
        } catch { toast.error("An error occurred while resending OTP"); }
        return;
      }
      if (data.message === "Invalid token" || data.message === "Invalid or expired token") {
        toast.info("Session Timeout", { position: "top-right", autoClose: false, closeOnClick: true });
        localStorage.removeItem("userToken");
        // Same courtesy as the logged-out path: remember the item and come back
        // here, so an expired session mid-add does not cost the sale either.
        try {
          localStorage.setItem(
            "riymay_pending_add",
            JSON.stringify({ productId: id, size: selectedSize, color: selectedColor })
          );
        } catch (e) { /* storage blocked */ }
        navigate("/login", { state: { from: { pathname: `/product/${id}` } } }); return;
      }
      localStorage.removeItem(`coupon_${id}_${selectedSize}`);

      // Was a toast that navigated to the cart when it closed — adding one item
      // ended the browsing session. The panel shows what went in and lets the
      // visitor choose.
      emitAddedToCart({
        name: product.name,
        image: product.images && product.images[0]
          ? `https://riymay.com/assets/${product.images[0].url}`
          : null,
        // The panel's thumbnail is 68px wide. Handing it the candidate list
        // lets it pull the smallest variant instead of the full photograph.
        // Null whenever the image has no variants, and the panel then renders
        // exactly what it rendered before.
        imageSrcSet: product.images ? buildSrcSet(product.images[0]) : null,
        size: selectedSize,
        color: selectedColor,
      });

      // Tell the navbar so the cart icon can acknowledge it, and hand it the
      // photo plus where that photo currently sits on screen. The navbar
      // clones it and arcs it into the cart icon — the "small thing going
      // into the cart".
      //
      // The measurement happens HERE, synchronously, still inside the click.
      // Nothing is deferred to a frame or a timer: those have silently never
      // fired on this site before, and an animation that depends on them is an
      // animation that does not exist.
      let rect = null;
      // In the stacked desktop gallery there is no carousel and no translated
      // track -- the images are simply laid out, so the first one still in
      // view is the one to fly from. Falling back like this keeps the
      // animation working in both layouts instead of only on phones.
      const stacked = carouselRef.current
        && carouselRef.current.classList.contains("pd-gallery");
      const el = carouselRef.current
        && (stacked
          ? [...carouselRef.current.querySelectorAll(".pd-gallery-img")].find((img) => {
              const r = img.getBoundingClientRect();
              return r.bottom > 0 && r.top < window.innerHeight;
            })
          : carouselRef.current.querySelectorAll(".pd-carousel-img")[slideIndex]);
      if (el && stacked) {
        const r = el.getBoundingClientRect();
        rect = { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width, height: r.height };
      } else if (el) {
        const r = el.getBoundingClientRect();
        // The track is a translated strip: only the visible slide is inside
        // the carousel frame. Measure the frame and keep the intersection so
        // the clone leaves from the square the visitor sees.
        const frame = carouselRef.current.getBoundingClientRect();
        const left = Math.max(r.left, frame.left);
        const top = Math.max(r.top, frame.top);
        const right = Math.min(r.right, frame.right);
        const bottom = Math.min(r.bottom, frame.bottom);
        if (right > left && bottom > top) {
          rect = { left, top, right, bottom, width: right - left, height: bottom - top };
        }
      }

      window.dispatchEvent(new CustomEvent("riymay:cart-changed", {
        detail: {
          image: product.images && product.images[0]
            ? `https://riymay.com/assets/${product.images[slideIndex] ? product.images[slideIndex].url : product.images[0].url}`
            : null,
          rect,
        },
      }));

    } catch {
      toast.error("An unexpected error occurred.");
    } finally {
      setDisable(false);
    }
  };

  // Returning from login: pick up a stashed Add-to-Cart for THIS product and
  // restore the chosen size/colour. Runs once (replayedRef guards it), and only
  // when logged in and the product has loaded. If anything here does not line
  // up, nothing happens and the shopper simply finds their size pre-selected
  // and taps Add -- they are never back at square one.
  useEffect(() => {
    if (replayedRef.current || !token || !product) return;
    let pending = null;
    try { pending = JSON.parse(localStorage.getItem("riymay_pending_add") || "null"); }
    catch (e) { pending = null; }
    if (!pending || String(pending.productId) !== String(id)) return;
    replayedRef.current = true;
    localStorage.removeItem("riymay_pending_add");
    setSelectedSize(pending.size);
    setColor(pending.color);
    setPendingReplay(true);
  }, [token, product, id]);

  // Finish the add once the restored size/colour are actually in state. Done in
  // its own effect so handleAddToCart reads the applied selection rather than
  // stale values from the same tick.
  useEffect(() => {
    if (!pendingReplay) return;
    // Wait for the page to finish loading too: handleAddToCart bails while
    // `loading` is true, and if we fired during that window the add would
    // silently no-op with no retry. Gating on !loading closes that race.
    if (!loading && selectedSize && selectedColor) {
      setPendingReplay(false);
      handleAddToCart(id);
    }
    // handleAddToCart is intentionally omitted: it is redefined every render and
    // we want this to fire only when the restored selection lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReplay, selectedSize, selectedColor, loading]);

  // Mirrors the real layout, so nothing jumps when the product lands.
  if (loading) return <SkeletonProduct />;
  if (error) return <NoData />;
  if (!product) return <div className="loader-container"><div className="traffic-loader"></div></div>;

  return (
    <>
      <div className="pd-layout">

        {/* LEFT: on a phone a swipe carousel; on a desktop every photo
            stacked in a scrolling column. See isWide above for why only one
            of the two is ever rendered. */}
        {isWide ? (
          <div className="pd-gallery" ref={carouselRef}>
            {product.images.map((image, i) => (
              <img
                key={i}
                src={`https://riymay.com/assets/${image.url}`}
                alt={`${product.name} view ${i + 1}`}
                className="pd-gallery-img"
                // The first photo is what the visitor is waiting on; the rest
                // are below the fold and can wait their turn.
                loading={i === 0 ? "eager" : "lazy"}
                {...responsiveProps(image, PD_GALLERY_SIZES)}
              />
            ))}
          </div>
        ) : (
        <div className="pd-media">
        <div
          className="pd-carousel"
          ref={carouselRef}
          onTouchStart={e => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            if (touchStartX.current === null) return;
            const diff = touchStartX.current - e.changedTouches[0].clientX;
            if (diff > 50) setSlideIndex(i => Math.min(i + 1, product.images.length - 1));
            else if (diff < -50) setSlideIndex(i => Math.max(i - 1, 0));
            touchStartX.current = null;
          }}
        >
          <div
            className="pd-carousel-track"
            style={{ transform: `translateX(-${slideIndex * 100}%)` }}
          >
            {product.images.map((image, i) => (
              <img
                key={i}
                src={`https://riymay.com/assets/${image.url}`}
                alt={`${product.name} view ${i + 1}`}
                className="pd-carousel-img"
                {...responsiveProps(image, PD_CAROUSEL_SIZES)}
              />
            ))}
          </div>

          {/* Arrows */}
          {slideIndex > 0 && (
            <button className="pd-arrow pd-arrow-left" onClick={() => setSlideIndex(i => i - 1)}>
              <BsChevronLeft size={18} />
            </button>
          )}
          {slideIndex < product.images.length - 1 && (
            <button className="pd-arrow pd-arrow-right" onClick={() => setSlideIndex(i => i + 1)}>
              <BsChevronRight size={18} />
            </button>
          )}

        </div>

          {/* Dots — below the image, not overlaid on it */}
          {product.images.length > 1 && (
            <div className="pd-dots">
              {product.images.map((_, i) => (
                <span
                  key={i}
                  className={`pd-dot ${i === slideIndex ? "pd-dot-active" : ""}`}
                  onClick={() => setSlideIndex(i)}
                />
              ))}
            </div>
          )}
        </div>
        )}

        {/* RIGHT: sticky info panel */}
        <div className="pd-info">
          <h1 className="pd-name">{product.name}</h1>
          <p className="pd-price">₹ {product.price}</p>

          <div className="pd-divider" />

          {/* Size */}
          <div className="pd-section">
            <div className="pd-section-header">
              <span className="pd-label">Size</span>
              <button className="pd-size-guide-btn" onClick={handleSizeChart}>Size Guide</button>
            </div>
            <div className="pd-sizes">
              {product.availableSizes.map(size => (
                <button
                  key={size}
                  className={`pd-size-btn ${selectedSize === size ? "pd-size-selected" : ""}`}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>

          {/* Color */}
          <div className="pd-section">
            <span className="pd-label">Color</span>
            <div className="pd-colors">
              {product.availableColors.map((color, i) => (
                <span
                  key={i}
                  onClick={() => setColor(color)}
                  className={`pd-color-swatch ${selectedColor === color ? "pd-color-selected" : ""}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          {stockStatusMessage && (
            <p className={`pd-stock ${stockStatusMessage === "In Stock" ? "pd-in-stock" : "pd-out-stock"}`}>
              {stockStatusMessage}
            </p>
          )}

          <button
            className={`pd-add-btn ${stockStatusMessage === "In Stock" ? "pd-add-active" : "pd-add-disabled"}`}
            onClick={() => handleAddToCart(product._id)}
          >
            {isDisable
              ? <BeatLoader size={8} color="#E8EBD6" />
              : !selectedSize
                ? "Select a size"
                : !selectedColor
                  ? "Select a colour"
                  : stockStatusMessage === "In Stock"
                    ? "Add to Cart"
                    : "Out of stock"}
          </button>

          <div className="pd-divider" />

          {/* Details */}
          <div className="pd-section">
            <span className="pd-label">Details</span>
            <p className="pd-desc">{product.description}</p>
          </div>

          {product.washCare && (
            <div className="pd-block">
              <p className="pd-block-label">Wash Care</p>
              <p className="pd-block-text">{product.washCare}</p>
            </div>
          )}

          {product.shipping && (
            <div className="pd-block">
              <p className="pd-block-label">Shipping</p>
              <p className="pd-block-text">{product.shipping}</p>
            </div>
          )}

          <div className="pd-section">
            <span className="pd-label">Category</span>
            <p className="pd-desc">{product.category}</p>
          </div>

          <div className="pd-divider" />

          {/* Share */}
          <div className="pd-share-wrap">
            <button className="pd-share-btn" onClick={() => setIsShareOpen(!isShareOpen)}>
              <FaLink size={11} /> Share
            </button>
            {isShareOpen && (
              <div className="pd-share-menu">
                <button className="pd-share-opt" onClick={shareToFacebook}><FaFacebookF size={13} /><span>Facebook</span></button>
                <button className="pd-share-opt" onClick={shareToTwitter}><FaTwitter size={13} /><span>Twitter</span></button>
                <button className="pd-share-opt" onClick={shareToWhatsApp}><FaWhatsapp size={13} /><span>WhatsApp</span></button>
                <button className="pd-share-opt" onClick={shareToPinterest}><FaPinterestP size={13} /><span>Pinterest</span></button>
                <CopyToClipboard text={shareUrl} onCopy={() => { setCopied(true); setTimeout(() => { setCopied(false); setIsShareOpen(false); }, 1500); }}>
                  <button className="pd-share-opt"><FaLink size={13} /><span>{copied ? "Copied!" : "Copy Link"}</span></button>
                </CopyToClipboard>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Related products */}
      <div className="pd-related">
        <h2 className="pd-related-heading">Related Products</h2>
        <div className="pd-related-grid">
          {relatedProducts.length > 0
            ? relatedProducts.map(p => (
                <Link className="related-product-container" key={p._id} to={`/product/${p._id}`}>
                  <RelatedProduct key={p._id} product={p} />
                </Link>
              ))
            : <p style={{ fontSize: 13, color: "rgba(31,70,41,0.4)" }}>No related products found.</p>
          }
        </div>
      </div>

      {/* Reviews */}
      <div className="pd-reviews">
        <h2 className="pd-reviews-heading">Ratings &amp; Reviews</h2>
        <Reviews productId={id} reviews={reviews} />
      </div>
    </>
  );
}

export default ProductDetails;
